import React, { useState } from "react";
import Button from "./Button";
import AddNewPaymentMethodModal from "./modals/AddNewPaymentMethodModal";
import { retrySubscriptionDispatch } from "app/subscriptions/actions";
import { useDispatch } from "react-redux";
import ErrorMessageModal from "./modals/ErrorMessageModal";
import Modal from "./Modal";
import { useStripe } from "@stripe/react-stripe-js";
import ReactivateFailedSubscriptionModal from "./modals/ReactivateFailedSubscriptionModal";
import RescheduleFailingSubscriptionModal from "./modals/RescheduleFailingSubscriptionModal";
import moment from "moment";

const getSubscriptionWarning = (subscription) => {
  const failure_reasons = {
    card_declined: { message: "Your card was declined." },
    card_removed: { message: "Your card was removed.", allowRetry: false },
    card_deleted: { message: "Your card was deleted.", allowRetry: false },
    card_expired: { message: "Your card has expired.", allowRetry: false },
    insufficient_funds: {
      message: "Your card has insufficient funds.",
      allowRetry: true
    },
    authentication_required: {
      message: "Your card requires SCA authentication."
    }
  };

  if (
    subscription.failure_reason &&
    ["failing", "failed"].indexOf(subscription.status) !== -1
  ) {
    return {
      type: "error",
      status: subscription.status,
      ...failure_reasons[subscription.failure_reason]
    };
  } else if (
    subscription.status === "active" &&
    subscription.payment_method &&
    subscription.payment_method.deleted_at
  ) {
    return {
      type: "warning",
      message: "Your selected payment card has been removed.",
      explanation:
        "Edit your payment method with a new card to avoid any interruptions.",
      status: subscription.status
    };
  } else if (subscription.status === "failed") {
    return {
      type: "error",
      message: "Your subscription payment has failed.",
      status: subscription.status
    };
  }
  // TODO: handle if card will expire soon
  return null;
};

const PaymentError = ({ subscription }) => {
  const paymentError = getSubscriptionWarning(subscription);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState(null);
  const [successState, setSuccessState] = useState(null);
  const [loadingButton, setLoadingButton] = useState(null);
  const dispatch = useDispatch();
  const stripe = useStripe();

  const nextPaymentDate =
    subscription?.next_dispatch?.status === "planned"
      ? moment(subscription.next_dispatch.order_date).format("Do MMMM YYYY")
      : null;

  if (!paymentError) {
    return null;
  }

  return (
    <div className="gkit-bg-red rounded px-3 py-2 d-flex justify-content-between align-items-center mb-3 flex-column flex-sm-row">
      <div className="flex-shrink-1">
        <p className="gkit-p-15 fw-bold text-white">
          {paymentError.message
            ? paymentError.message
            : "Your last payment failed"}
        </p>
        {paymentError.explanation && (
          <p className="gkit-p-15 text-white mt-1">
            {paymentError.explanation}
          </p>
        )}
        {nextPaymentDate ? (
          <p className="gkit-p-13 text-white mt-1">
            Next payment attempt: {nextPaymentDate}
          </p>
        ) : null}
      </div>
      <div className="ms-sm-3 mt-2 mt-sm-0 flex-shrink-1 d-flex flex-column w-100-mobile">
        <Button
          btnType="tertiary"
          className="bg-white"
          onClick={() => setModal("addNew")}
        >
          Add new card
        </Button>

        {paymentError.status === "failed" &&
        paymentError.allowRetry !== false &&
        subscription.payment_method ? (
          <Button
            btnType="tertiary"
            className="bg-white mt-2"
            onClick={() => setModal("reactivateFailed")}
          >
            Pick a retry date
          </Button>
        ) : null}

        {paymentError.status === "failing" &&
          paymentError.allowRetry !== false &&
          subscription.payment_method && (
            <>
              <Button
                btnType="tertiary"
                className="bg-white mt-2"
                onClick={() => setModal("rescheduleFailing")}
              >
                Pick a retry date
              </Button>
              <Button
                btnType="tertiary"
                className="mt-2 bg-white"
                isLoading={loadingButton === "updateSubscriptionPaymentMethod"}
                onClick={() => {
                  setLoadingButton("updateSubscriptionPaymentMethod");
                  dispatch(retrySubscriptionDispatch(subscription.id, stripe))
                    .then(
                      () =>
                        setSuccessState({
                          title:
                            "The payment has been successfully processed for the subscription"
                        }),
                      (err) => {
                        console.log("Err: ", err);
                        setError(err);
                      }
                    )
                    .finally(() => setLoadingButton(null));
                }}
              >
                Retry payment now
              </Button>
            </>
          )}
      </div>
      <AddNewPaymentMethodModal
        isOpen={modal === "addNew"}
        subscriptionId={subscription.id}
        handleClose={() => setModal(null)}
      />
      <ReactivateFailedSubscriptionModal
        isOpen={modal === "reactivateFailed"}
        subscriptionId={subscription.id}
        onClose={() => setModal(null)}
      />
      <RescheduleFailingSubscriptionModal
        isOpen={modal === "rescheduleFailing"}
        subscriptionId={subscription.id}
        onClose={() => setModal(null)}
      />
      {error && (
        <ErrorMessageModal
          error={error}
          handleClose={() => {
            setModal(null);
            setError(null);
          }}
        />
      )}
      {successState && (
        <Modal
          isOpen={successState}
          handleClose={() => {
            setModal(null);
            setSuccessState(null);
          }}
          successState={successState}
        />
      )}
    </div>
  );
};

export default PaymentError;
